import React, { useState, useEffect, useRef } from 'react';
import { callApi } from '../api';
import {
    Layout, Image as ImageIcon, Image as ImageImageIcon, Shield, Zap, Settings,
    Database, Power, Snowflake, Bell, Globe, Lock,
    Cpu, HardDrive, ArrowLeft, Share2, Code, Archive,
    Trash, Map, FileText, Mail, ShieldAlert, Gauge,
    Megaphone, LayoutTemplate, Menu, Users, Puzzle, Server,
    Save, X, Smartphone, Moon, MousePointer, QrCode, MessageCircle, Star,
    Sparkles, Utensils, TrendingUp, UserCheck, Activity, Clock
} from 'lucide-react';
import './SiteYonetim.css';

const SiteYonetim = ({ kulup }) => {
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [activeModal, setActiveModal] = useState(null);
    const [loadingAction, setLoadingAction] = useState(null);

    // --- FEATURES STATE ---
    const defaultFeatures = React.useMemo(() => ({
        snowEffect: false,
        confettiEffect: false,
        maintenanceMode: false,
        popupAnnouncement: true,
        darkMode: false,
        customCursor: false,
        instagramFeed: false,
        digitalId: false,
        whatsappBtn: false,
        starRating: false,
        pwaMode: false,
        rightClickProtect: false,
        autoBackup: true,
        lazyLoad: true,
        minifyCss: false
    }), []);

    const [features, setFeatures] = useState(defaultFeatures);

    // --- FORM STATES ---
    const [seoSettings, setSeoSettings] = useState({ title: 'Klu Gastronomi', desc: '', keywords: '' });
    const [socialLinks, setSocialLinks] = useState({ instagram: '', twitter: '', linkedin: '', youtube: '' });
    const [announcement, setAnnouncement] = useState('');
    const [customCss, setCustomCss] = useState('/* Custom CSS here */');
    const [smtpSettings, setSmtpSettings] = useState({ host: 'smtp.gmail.com', port: '587', user: '' });
    const [maintenanceMessage, setMaintenanceMessage] = useState('Sitemiz şu anda bakımdadır. Lütfen daha sonra tekrar deneyin.');
    const [instagramWidgetCode, setInstagramWidgetCode] = useState(''); // NEW: Instagram Widget Code
    const [instagramImages, setInstagramImages] = useState(Array(6).fill('')); // Mobile Instagram Images State

    // --- AI CHEF STATE ---
    const [aiPrompt, setAiPrompt] = useState({ ingredients: '', cuisine: 'Türk Mutfağı', type: 'Ana Yemek', difficulty: 'Orta' });
    const [aiResult, setAiResult] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);

    // --- REAL SYSTEM STATS (Fetched from API) ---
    const [stats, setStats] = useState({
        totalMembers: 0,
        pendingApps: 0,
        activeEvents: 0,
        latency: 0,
        serverStatus: 'Yükleniyor...'
    });

    // --- LOAD ALL SETTINGS & STATS ---
    useEffect(() => {
        const fetchAllData = async () => {
            // 1. Settings from DB
            try {
                const results = await callApi('/settings', 'GET');
                if (results) {
                    if (results.features) setFeatures({ ...defaultFeatures, ...results.features });
                    if (results.seoSettings) setSeoSettings(results.seoSettings);
                    if (results.socialLinks) setSocialLinks(results.socialLinks);
                    if (results.announcement) setAnnouncement(results.announcement);
                    if (results.customCss) setCustomCss(results.customCss);
                    if (results.announcement) setAnnouncement(results.announcement);
                    if (results.customCss) setCustomCss(results.customCss);
                    if (results.smtpSettings) setSmtpSettings(results.smtpSettings);
                    if (results.maintenanceMessage) setMaintenanceMessage(results.maintenanceMessage);
                    if (results.instagramWidgetCode) setInstagramWidgetCode(results.instagramWidgetCode);
                    if (results.instagramImages) setInstagramImages(results.instagramImages);
                }
            } catch (e) { console.error("Settings load error", e); }

            // 2. Real System Stats
            const startTime = Date.now();
            try {
                const [usersData, appsData, eventsData] = await Promise.all([
                    callApi('/uyeler', 'GET'),
                    callApi('/basvurular', 'GET'),
                    callApi('/etkinlikler', 'GET')
                ]);

                const endTime = Date.now();
                const latency = endTime - startTime;

                const users = usersData.items || [];
                const apps = appsData.items || [];
                const events = eventsData.items || [];

                const pending = apps.filter(a => a.durum === 'BEKLIYOR').length;
                const active = events.filter(e => new Date(e.etkinlikTarihi || e.tarih) > new Date()).length;

                setStats({
                    totalMembers: users.length,
                    pendingApps: pending,
                    activeEvents: active,
                    latency: latency,
                    serverStatus: 'Online'
                });

            } catch (err) {
                console.warn("Stats fetch failed", err);
                setStats(prev => ({ ...prev, serverStatus: 'Bağlantı Hatası' }));
            }
        };

        fetchAllData();

        // Specific Content Fetch for Instagram Widget & Feed
        const fetchContentData = async () => {
            try {
                const res = await callApi('/content', 'GET');
                if (res && res.success && res.content) {
                    if (res.content['site-instagram-widget']) {
                        setInstagramWidgetCode(res.content['site-instagram-widget']);
                    }
                    if (res.content['site-instagram-feed']) {
                        setInstagramImages(JSON.parse(res.content['site-instagram-feed']));
                    }
                }
            } catch (e) {
                console.warn("Content fetch failed", e);
            }
        };
        fetchContentData();

        // Poll real stats every 30 seconds
        const interval = setInterval(fetchAllData, 30000);
        return () => clearInterval(interval);
    }, []);

    const toggleFeature = async (key) => {
        const newState = { ...features, [key]: !features[key] };
        setFeatures(newState);
        try { await callApi('/settings', 'PUT', { key: 'features', value: newState }); }
        catch (e) { alert('Ayar kaydedilemedi: ' + e.message); }
    };

    const handleSimulatedAction = async (actionName, successMessage) => {
        setLoadingAction(actionName);
        await new Promise(r => setTimeout(r, 2000));
        setLoadingAction(null);
        alert(successMessage || 'İşlem başarıyla tamamlandı!');
    };

    const saveSettings = async (key, value, modalName) => {
        try {
            await callApi('/settings', 'PUT', { key, value });
            setActiveModal(null);
            alert(`${modalName} ayarları kaydedildi.`);
        } catch (e) { alert('Kaydetme başarısız: ' + e.message); }
    };

    // --- AI CHEF LOGIC ---
    const generateRecipe = async () => {
        setIsGenerating(true);
        setAiResult(null);

        // Simulate AI Delay
        await new Promise(r => setTimeout(r, 2500));

        // Mock Response based on inputs & simple templates
        const title = `${aiPrompt.cuisine} Usulü ${aiPrompt.type}`;
        const ingredientsList = aiPrompt.ingredients.split(',').map(i => i.trim()).filter(i => i);

        const mockRecipe = {
            title: title + (ingredientsList.length > 0 ? ` (${ingredientsList[0]} İlaveli)` : ''),
            desc: `Bu özel ${aiPrompt.difficulty} seviyesindeki tarif, ${aiPrompt.cuisine} mutfağının en seçkin örneklerinden biridir.`,
            ingredients: [
                ...ingredientsList.map(i => `200g ${i}`),
                "1 tutam sevgi",
                "2 yemek kaşığı zeytinyağı",
                "Tuz ve Karabiber"
            ],
            steps: [
                "1. Fırını önceden 180 dereceye ısıtın.",
                `2. ${ingredientsList.join(' ve ')} malzemelerini doğrayın.`,
                "3. Geniş bir kapta zeytinyağı ile harmanlayın.",
                "4. 30 dakika pişirdikten sonra sıcak servis yapın."
            ]
        };

        setAiResult(mockRecipe);
        setIsGenerating(false);
    };

    // --- MODAL COMPONENT ---
    const Modal = ({ title, children, onSave, showSave = true }) => (
        <div className="modal-overlay" onClick={() => setActiveModal(null)}>
            <div className={`modal-content ${title.includes('AI') ? 'modal-lg' : ''}`} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{title}</h3>
                    <button onClick={() => setActiveModal(null)}><X size={20} /></button>
                </div>
                <div className="modal-body">
                    {children}
                </div>
                <div className="modal-footer">
                    <button className="cancel-btn" onClick={() => setActiveModal(null)}>Kapat</button>
                    {showSave && <button className="save-btn" onClick={onSave}><Save size={16} /> Kaydet</button>}
                </div>
            </div>
        </div>
    );

    const iframeRef = useRef(null);

    // --- VISUAL EDITOR STATE ---
    const [selectedElement, setSelectedElement] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [editStyle, setEditStyle] = useState({});

    // Listen for messages from Iframe
    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data && event.data.type === 'ELEMENT_CLICKED') {
                const { key, currentValue, currentStyle } = event.data;
                setSelectedElement(key);
                setEditValue(currentValue);
                try { setEditStyle(JSON.parse(currentStyle || '{}')); } catch (e) { setEditStyle({}); }
            }
        };
        window.onmessage = handleMessage;
        return () => { window.onmessage = null; };
    }, []);

    const handleSaveElement = async () => {
        if (!selectedElement) return;
        try {
            await callApi('/content', 'PUT', { key: selectedElement, value: editValue });
            await callApi('/content', 'PUT', { key: `${selectedElement}_style`, value: JSON.stringify(editStyle) });
            alert('Güncelleme başarılı!');
            // Fix for Cross-Origin "Blocked a frame" error:
            if (iframeRef.current) iframeRef.current.src = iframeRef.current.src;
            setSelectedElement(null);
        } catch (e) { alert('Hata: ' + e.message); }
    };

    const updateStyle = (prop, val) => {
        setEditStyle(prev => ({ ...prev, [prop]: val }));
        if (iframeRef.current) {
            iframeRef.current.contentWindow.postMessage({
                type: 'PREVIEW_STYLE', key: selectedElement, style: { ...editStyle, [prop]: val }
            }, '*');
        }
    };

    // --- RENDER ---
    if (!isEditorOpen) {
        return (
            <div className="cpanel-container">
                <div className="cpanel-header">
                    <h1>⚙️ Site Kontrol Merkezi (cPanel)</h1>
                    <p style={{ marginTop: '0.5rem', color: '#6b7280' }}>Gelişmiş yönetim paneli v2.2 • Gerçek Zamanlı Veriler</p>
                </div>

                {/* REAL SYSTEM STATS (NOW ACTUALLY REAL) */}
                <div className="system-load analytics-bar">
                    <div className="load-item">
                        <div className="load-value" style={{ color: '#2563eb' }}>{stats.totalMembers}</div>
                        <div className="load-label"><Users size={14} /> Toplam Üye</div>
                    </div>
                    <div className="divider"></div>
                    {/* Pending Apps Removed as per request */}
                    <div className="load-item">
                        <div className="load-value">{stats.activeEvents}</div>
                        <div className="load-label"><Activity size={14} /> Aktif Etkinlik</div>
                    </div>
                    <div className="divider"></div>
                    <div className="load-item">
                        <div className="load-value" style={{ color: stats.latency < 200 ? '#10b981' : '#dc2626', fontSize: '0.9rem' }}>
                            {stats.latency} ms
                        </div>
                        <div className="load-label"><Gauge size={14} /> Sistem Gecikmesi</div>
                    </div>
                </div>

                <div className="cpanel-grid-wrapper">

                    {/* 1. ÖZELLİKLER (FEATURES) */}
                    <div className="cpanel-section">
                        <div className="section-title">✨ Görsel & Eğlence</div>
                        <div className="module-grid">
                            <ModuleToggle title="Kış Modu" icon={<Snowflake />} text="Kar yağışı efekti." active={features.snowEffect} onClick={() => toggleFeature('snowEffect')} />
                            <ModuleToggle title="Konfeti" icon={<Settings />} text="Başvuruda konfeti patlat." active={features.confettiEffect} onClick={() => toggleFeature('confettiEffect')} />
                            <ModuleToggle title="Gece Modu" icon={<Moon />} text="Dark Mode desteği." active={features.darkMode} onClick={() => toggleFeature('darkMode')} />
                            <ModuleToggle title="Özel Mouse" icon={<MousePointer />} text="Bıçak/Çatal imleci." active={features.customCursor} onClick={() => toggleFeature('customCursor')} />
                        </div>
                    </div>

                    <div className="cpanel-section">
                        <div className="section-title">🤖 AI & Otomasyon <span className="badge-new">YENİ</span></div>
                        <div className="module-grid">
                            <ModuleCard
                                title="AI Şef"
                                icon={<Sparkles color="#8b5cf6" />}
                                text="Yapay Zeka Tarif Üretici."
                                onClick={() => setActiveModal('AI_CHEF')}
                                bg="linear-gradient(135deg, #fff 0%, #f3e8ff 100%)"
                            />
                            <ModuleToggle title="Dijital Kart" icon={<QrCode />} text="QR Üye Kimliği." active={features.digitalId} onClick={() => toggleFeature('digitalId')} />
                            <ModuleToggle title="WhatsApp" icon={<MessageCircle />} text="Yüzen WhatsApp butonu." active={features.whatsappBtn} onClick={() => toggleFeature('whatsappBtn')} />
                        </div>
                    </div>

                    <div className="cpanel-section">
                        <div className="section-title">🌍 Genel & SEO</div>
                        <div className="module-grid">
                            <ModuleCard title="SEO Ayarları" icon={<Globe />} text="Başlık, Açıklama, Meta." onClick={() => setActiveModal('SEO')} />
                            <ModuleCard title="Sosyal Medya" icon={<Share2 />} text="Instagram, Twitter linkleri." onClick={() => setActiveModal('SOCIAL')} />
                            <ModuleCard title="Site Haritası" icon={<Map />} text="Sitemap.xml oluştur." onClick={() => handleSimulatedAction('sitemap', 'Sitemap.xml başarıyla oluşturuldu.')} loading={loadingAction === 'sitemap'} />
                        </div>
                    </div>

                    <div className="cpanel-section">
                        <div className="section-title">🎨 Tasarım & İçerik</div>
                        <div className="module-grid">
                            <ModuleCard title="Canlı Tasarımcı" icon={<Layout style={{ color: '#2563eb' }} />} text="Logo ve Renk düzenleyici." onClick={() => setIsEditorOpen(true)} bg="#eff6ff" />
                            <ModuleCard title="Insta Feed" icon={<ImageImageIcon />} text="Duvar fotoğraflarını seç." onClick={() => setActiveModal('INSTA_FEED')} />
                            <ModuleCard title="Duyuru Barı" icon={<Megaphone />} text="Tepe duyuru metni." onClick={() => setActiveModal('ANNOUNCE')} />
                            <ModuleCard title="Fotoğraflar" icon={<Archive />} text="Media Kütüphanesi" onClick={() => window.location.href = '/medya'} />
                            {/* Force Refresh */}
                            <ModuleCard title="Özel CSS" icon={<Code />} text="Custom CSS/JS ekle." onClick={() => setActiveModal('CSS')} />
                        </div>
                    </div>

                    {/* SYSTEM CONTROLS */}
                    <div className="cpanel-section">
                        <div className="section-title">🛠️ Sistem Kontrolleri</div>
                        <div className="module-grid">
                            <ModuleToggle
                                title="Bakım Modu"
                                icon={<Power color={features.maintenanceMode ? '#dc2626' : undefined} />}
                                text={features.maintenanceMode ? 'Site kapalı!' : 'Siteyi bakıma al.'}
                                active={features.maintenanceMode}
                                onClick={() => toggleFeature('maintenanceMode')}
                            />
                            <ModuleCard
                                title="Bakım Mesajı"
                                icon={<FileText />}
                                text="Bakım ekranı yazısı."
                                onClick={() => setActiveModal('MAINTENANCE_MSG')}
                            />
                        </div>
                    </div>
                </div>

                {/* --- MODALS --- */}

                {/* AI CHEF MODAL */}
                {activeModal === 'AI_CHEF' && (
                    <Modal title="👨‍🍳 AI Şef (Tarif Üretici)" showSave={false}>
                        {!aiResult ? (
                            <div className="ai-form">
                                <p style={{ marginBottom: '1rem', color: '#666' }}>Elinizdeki malzemeleri girin, Yapay Zeka sizin için özel bir tarif oluştursun.</p>
                                <div className="form-group">
                                    <label>Malzemeler (Virgülle ayırın)</label>
                                    <input
                                        value={aiPrompt.ingredients}
                                        onChange={e => setAiPrompt({ ...aiPrompt, ingredients: e.target.value })}
                                        placeholder="Örn: Tavuk, Krema, Mantar, Fesleğen"
                                    />
                                </div>
                                <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div className="form-group">
                                        <label>Mutfak Türü</label>
                                        <select value={aiPrompt.cuisine} onChange={e => setAiPrompt({ ...aiPrompt, cuisine: e.target.value })}>
                                            <option>Türk Mutfağı</option>
                                            <option>İtalyan Mutfağı</option>
                                            <option>Fransız Mutfağı</option>
                                            <option>Uzak Doğu</option>
                                            <option>Modern Füzyon</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Zorluk Seviyesi</label>
                                        <select value={aiPrompt.difficulty} onChange={e => setAiPrompt({ ...aiPrompt, difficulty: e.target.value })}>
                                            <option>Kolay</option>
                                            <option>Orta</option>
                                            <option>Şef Usulü (Zor)</option>
                                        </select>
                                    </div>
                                </div>
                                <button className="ai-generate-btn" onClick={generateRecipe} disabled={isGenerating}>
                                    {isGenerating ? (
                                        <><div className="spinner-sm"></div> Şef Düşünüyor...</>
                                    ) : (
                                        <><Sparkles size={18} /> Tarif Oluştur</>
                                    )}
                                </button>
                            </div>
                        ) : (
                            <div className="ai-result animate-fade-in">
                                <div className="recipe-card">
                                    <div className="recipe-header">
                                        <h4>{aiResult.title}</h4>
                                        <span className="badge">{aiPrompt.cuisine}</span>
                                    </div>
                                    <p className="recipe-desc">{aiResult.desc}</p>

                                    <h5>Malzemeler</h5>
                                    <ul>{aiResult.ingredients.map((ing, i) => <li key={i}>{ing}</li>)}</ul>

                                    <h5>Hazırlanışı</h5>
                                    <ol>{aiResult.steps.map((step, i) => <li key={i}>{step}</li>)}</ol>

                                    <div className="recipe-actions">
                                        <button className="primary-btn-sm" onClick={() => { alert('Taslaklara kaydedildi!'); setActiveModal(null); setAiResult(null); }}>
                                            <Save size={14} /> Taslak Olarak Kaydet
                                        </button>
                                        <button className="text-btn-sm" onClick={() => setAiResult(null)}>Yeni Tarif Dene</button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Modal>
                )}

                {/* EXISTING MODALS */}
                {activeModal === 'SEO' && (
                    <Modal title="SEO Ayarları" onSave={() => saveSettings('seoSettings', seoSettings, 'SEO')}>
                        <div className="form-group"><label>Site Başlığı</label><input value={seoSettings.title} onChange={e => setSeoSettings({ ...seoSettings, title: e.target.value })} /></div>
                        <div className="form-group"><label>Açıklama</label><textarea value={seoSettings.desc} onChange={e => setSeoSettings({ ...seoSettings, desc: e.target.value })} rows={3} /></div>
                        <div className="form-group"><label>Anahtar Kelimeler</label><input value={seoSettings.keywords} onChange={e => setSeoSettings({ ...seoSettings, keywords: e.target.value })} placeholder="Virgülle ayırın" /></div>
                    </Modal>
                )}
                {activeModal === 'SOCIAL' && (
                    <Modal title="Sosyal Medya" onSave={() => saveSettings('socialLinks', socialLinks, 'Sosyal Medya')}>
                        <div className="form-group"><label>Instagram</label><input value={socialLinks.instagram} onChange={e => setSocialLinks({ ...socialLinks, instagram: e.target.value })} /></div>
                        <div className="form-group"><label>Twitter</label><input value={socialLinks.twitter} onChange={e => setSocialLinks({ ...socialLinks, twitter: e.target.value })} /></div>
                        <div className="form-group"><label>LinkedIn</label><input value={socialLinks.linkedin} onChange={e => setSocialLinks({ ...socialLinks, linkedin: e.target.value })} /></div>
                    </Modal>
                )}
                {activeModal === 'ANNOUNCE' && (
                    <Modal title="Duyuru Barı" onSave={() => saveSettings('announcement', announcement, 'Duyuru')}>
                        <div className="form-group"><label>Duyuru Metni</label><input value={announcement} onChange={e => setAnnouncement(e.target.value)} placeholder="Örn: Sınav haftası boyunca kütüphane 24 saat açıktır." /></div>
                    </Modal>
                )}
                {activeModal === 'CSS' && (
                    <Modal title="Özel CSS" onSave={() => saveSettings('customCss', customCss, 'Özel CSS')}>
                        <div className="form-group"><label>CSS Kodu</label><textarea value={customCss} onChange={e => setCustomCss(e.target.value)} rows={10} style={{ fontFamily: 'monospace', fontSize: '0.9rem' }} /></div>
                    </Modal>
                )}
                {activeModal === 'INSTA_FEED' && (
                    <Modal title="Instagram Duvarı (Mobil & Masaüstü)" onSave={async () => {
                        // Save both values
                        await saveSettings('instagramWidgetCode', instagramWidgetCode, 'Instagram Feed');
                        await saveSettings('instagramImages', instagramImages, 'Instagram Mobil Feed');
                        try {
                            await callApi('/content', 'PUT', { key: 'site-instagram-widget', value: instagramWidgetCode });
                            await callApi('/content', 'PUT', { key: 'site-instagram-feed', value: JSON.stringify(instagramImages) });
                            alert("Instagram akışı güncellendi!");
                        } catch (e) { console.error("Content persist failed", e); }
                    }}>
                        {/* Section 1: Desktop Widget */}
                        <h4 style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>Masaüstü Görünümü (Otomatik Widget)</h4>
                        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                            <a href="https://lightwidget.com/" target="_blank" rel="noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>LightWidget</a> veya <strong>Elfsight</strong> gibi bir servisten aldığınız HTML Iframe kodunu buraya yapıştırın.
                        </p>
                        <div className="form-group" style={{ height: '120px', marginBottom: '1.5rem' }}>
                            <textarea
                                value={instagramWidgetCode}
                                onChange={e => setInstagramWidgetCode(e.target.value)}
                                placeholder="<script src='//...></script><div...>"
                                style={{ fontSize: '0.85rem', fontFamily: 'monospace', width: '100%', height: '100%', resize: 'none' }}
                            />
                        </div>

                        <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', marginBottom: '1.5rem' }} />

                        {/* Section 2: Mobile Grid (Manual 6 Images) */}
                        <h4 style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>Mobil Görünüm (6 Sabit Görsel)</h4>
                        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
                            (Mobil cihazlarda özel widget sorunu yaşanmaması için) Eski düzendeki gibi mobilde gösterilecek fotoğrafların URL'lerini girin.
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', maxHeight: '400px', overflowY: 'auto', paddingRight: '0.5rem' }}>
                            {instagramImages.map((url, i) => (
                                <div key={i} className="form-group">
                                    <label>Fotoğraf {i + 1}</label>
                                    <input
                                        value={url}
                                        onChange={e => {
                                            const newImages = [...instagramImages];
                                            newImages[i] = e.target.value;
                                            setInstagramImages(newImages);
                                        }}
                                        placeholder="https://..."
                                        style={{ fontSize: '0.8rem' }}
                                    />
                                    {url && (
                                        <div style={{ marginTop: '0.5rem', height: '60px', borderRadius: '4px', overflow: 'hidden', background: '#f3f4f6' }}>
                                            <img src={url} alt={`Preview ${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => e.target.style.display = 'none'} />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Modal>
                )}
                {activeModal === 'MAINTENANCE_MSG' && (
                    <Modal title="Bakım Modu Mesajı" onSave={() => saveSettings('maintenanceMessage', maintenanceMessage, 'Bakım Mesajı')}>
                        <div className="form-group">
                            <label>Bakım Mesajı</label>
                            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>Bakım modunda ziyaretçilere gösterilecek mesaj.</p>
                            <textarea
                                value={maintenanceMessage}
                                onChange={e => setMaintenanceMessage(e.target.value)}
                                rows={4}
                                placeholder="Sitemiz şu anda bakımdadır. Lütfen daha sonra tekrar deneyin."
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ marginTop: '1rem', padding: '1rem', background: '#fef3c7', borderRadius: '8px', border: '1px solid #f59e0b' }}>
                            <strong>⚠️ Not:</strong> Bakım modunu aktif etmek için yukarıdaki "Bakım Modu" butonunu açın.
                        </div>
                    </Modal>
                )}
            </div>
        );
    }

    // Editor Iframe
    return (
        <div className="editor-mode">
            <div className="editor-toolbar">
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <button onClick={() => setIsEditorOpen(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <ArrowLeft /> cPanel'e Dön
                    </button>
                    <span style={{ fontWeight: 'bold' }}>Canlı Düzenleyici</span>
                </div>
                <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                    Düzeltmek istediğiniz metne veya başlığa tıklayın.
                </div>
            </div>

            <div style={{ flex: 1, position: 'relative', display: 'flex' }}>
                <iframe
                    ref={iframeRef}
                    src="https://klugastronomi-site.pages.dev?mode=edit"
                    title="Editor"
                    style={{ flex: 1, border: 'none' }}
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                />
                {/* Editor sidebar code (same as before) */}
                {selectedElement && (
                    <div className="editor-sidebar animate-slide-in">
                        <div className="sidebar-header">
                            <h3>Düzenle: {selectedElement}</h3>
                            <button className="close-btn" onClick={() => setSelectedElement(null)}><X size={16} /></button>
                        </div>
                        <div className="sidebar-content">
                            <div className="form-group"><label>İçerik Metni</label><textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} rows={4} /></div>
                            <div className="divider"></div>
                            <h4>Görünüm</h4>
                            <div className="form-group"><label>Renk</label><div className="color-picker-wrapper"><input type="color" value={editStyle.color || '#000000'} onChange={(e) => updateStyle('color', e.target.value)} /><input type="text" value={editStyle.color || ''} onChange={(e) => updateStyle('color', e.target.value)} placeholder="#000000" /></div></div>
                            <div className="form-group"><label>Yazı Boyutu</label><select value={editStyle.fontSize || ''} onChange={(e) => updateStyle('fontSize', e.target.value)}><option value="">Varsayılan</option><option value="12px">12px</option><option value="16px">16px</option><option value="20px">20px</option><option value="24px">24px</option></select></div>
                            <button className="reset-style-btn" onClick={() => { setEditStyle({}); }}>Stili Sıfırla</button>
                        </div>
                        <div className="sidebar-footer">
                            <button className="save-btn w-full" onClick={handleSaveElement}><Save size={16} /> Değişiklikleri Kaydet</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Helper Components
const ModuleCard = ({ title, icon, text, onClick, loading, bg }) => (
    <div className="module-card" onClick={onClick} style={{ background: bg }}>
        <div className="module-icon">{loading ? <div className="spinner-sm"></div> : icon}</div>
        <div className="module-info">
            <h3>{title}</h3>
            <p>{loading ? 'İşlem yapılıyor...' : text}</p>
        </div>
    </div>
);

const ModuleToggle = ({ title, icon, text, active, onClick }) => (
    <div className="module-card" onClick={onClick}>
        <div className="module-icon">{icon}</div>
        <div className="module-info">
            <h3>{title}</h3>
            <p>{text}</p>
        </div>
        <div className="feature-toggle">
            <div className={`toggle-switch ${active ? 'active' : ''}`}>
                <div className="toggle-knob"></div>
            </div>
        </div>
    </div>
);

export default SiteYonetim;
